/**
 * The only module permitted to make external network calls.
 *
 * Every other file is blocked by ESLint from using fetch/XMLHttpRequest/etc.
 * directly. To add a network feature (license check, telemetry, settings sync,
 * remote action update), import a helper from here.
 *
 * To add a destination:
 *   1. Add its hostname to ALLOWLIST below.
 *   2. Add the same hostname to manifest.xml <AppDomains>. Office runtime
 *      enforces that list independently of this code.
 *   3. Code review the change — this is the only place outbound traffic happens.
 */

import { getToken, signOut } from "./auth";

/**
 * Centralized 401 handler. When the server says our token is no longer
 * valid (expired, revoked, user blocked), drop local session state so
 * the sign-in UI takes over again. Re-throws the original error so the
 * calling code still sees the failure.
 */
async function handle401AndThrow(url: URL, status: number): Promise<never> {
  if (status === 401) {
    void signOut().catch(() => undefined);
  }
  throw new Error(`outbound: ${url.hostname} → ${status}`);
}

const ALLOWLIST: ReadonlySet<string> = new Set<string>([
  // Local PDF extraction server (dev mode).
  "localhost",
  // CogSeven Companion v0 production host. Bundle, API, and Clerk auth all
  // live behind this single hostname (Caddy reverse-proxies /api/* to the
  // FastAPI container). Must match manifest.xml AppDomains in production
  // builds.
  "app.cogsevencompanion.com",
]);

/**
 * Build the Authorization header from the current Clerk (or dev-stub)
 * session, if signed in. Returns an empty object when signed out so
 * callers can spread it unconditionally:  { ...await authHeader() }.
 */
async function authHeader(): Promise<Record<string, string>> {
  const token = await getToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

function assertAllowed(rawUrl: string): URL {
  const url = new URL(rawUrl);
  if (url.protocol !== "https:") {
    throw new Error(`outbound: refusing non-HTTPS URL (${rawUrl})`);
  }
  if (!ALLOWLIST.has(url.hostname)) {
    throw new Error(`outbound: hostname not in allowlist (${url.hostname})`);
  }
  return url;
}

export async function getJson<T>(rawUrl: string, init: RequestInit = {}): Promise<T> {
  const url = assertAllowed(rawUrl);
  const headers = { ...(init.headers ?? {}), ...(await authHeader()) };
  const res = await fetch(url, { ...init, method: "GET", credentials: "omit", headers });
  if (!res.ok) await handle401AndThrow(url, res.status);
  return res.json() as Promise<T>;
}

export async function postJson<TRes, TReq = unknown>(
  rawUrl: string,
  body: TReq,
  init: RequestInit = {}
): Promise<TRes> {
  const url = assertAllowed(rawUrl);
  const res = await fetch(url, {
    ...init,
    method: "POST",
    credentials: "omit",
    headers: {
      "content-type": "application/json",
      ...(init.headers ?? {}),
      ...(await authHeader()),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) await handle401AndThrow(url, res.status);
  return res.json() as Promise<TRes>;
}

export async function deleteJson<T>(rawUrl: string, init: RequestInit = {}): Promise<T> {
  const url = assertAllowed(rawUrl);
  const headers = { ...(init.headers ?? {}), ...(await authHeader()) };
  const res = await fetch(url, { ...init, method: "DELETE", credentials: "omit", headers });
  if (!res.ok) await handle401AndThrow(url, res.status);
  return res.json() as Promise<T>;
}

export async function patchJson<TRes, TReq = unknown>(
  rawUrl: string,
  body: TReq,
  init: RequestInit = {}
): Promise<TRes> {
  const url = assertAllowed(rawUrl);
  const res = await fetch(url, {
    ...init,
    method: "PATCH",
    credentials: "omit",
    headers: {
      "content-type": "application/json",
      ...(init.headers ?? {}),
      ...(await authHeader()),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) await handle401AndThrow(url, res.status);
  return res.json() as Promise<TRes>;
}

export interface PostFileOptions {
  fieldName?: string;
  headers?: Record<string, string>;
  formFields?: Record<string, string>;
}

export async function postFile<T>(
  rawUrl: string,
  file: File,
  options: PostFileOptions = {}
): Promise<T> {
  const url = assertAllowed(rawUrl);
  const formData = new FormData();
  formData.append(options.fieldName ?? "file", file, file.name);
  for (const [k, v] of Object.entries(options.formFields ?? {})) {
    formData.append(k, v);
  }
  const headers = { ...(options.headers ?? {}), ...(await authHeader()) };
  const res = await fetch(url, {
    method: "POST",
    credentials: "omit",
    headers,
    body: formData,
  });
  if (!res.ok) {
    if (res.status === 401) void signOut().catch(() => undefined);
    const body = await res.text().catch(() => "");
    throw new Error(
      `outbound: POST ${url.hostname} → ${res.status}${body ? `: ${body.slice(0, 200)}` : ""}`
    );
  }
  return res.json() as Promise<T>;
}
